import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { BackLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { Table } from '~/components/ui/Table';

// The STUDENT shape: `lesson_questions` no longer carries `is_correct` (and
// carries no answer rows at all for cloze), which costs this list nothing — it
// renders order/id/type/text/score. The answer key is on the detail screen,
// which reads the admin-only `question_detail`.
type Question = components['schemas']['Question'];

export const Route = createFileRoute('/_admin/questions/')({
  validateSearch: (s: Record<string, unknown>) => ({
    lesson: Number(s.lesson),
  }),
  component: QuestionsList,
});

const columnHelper = createColumnHelper<Question>();

const columns = [
  columnHelper.accessor('order', { header: 'Order' }),
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('type', { header: 'Type' }),
  columnHelper.accessor('text', {
    header: 'Question',
    cell: (info) => {
      const text = info.getValue() ?? '';
      return text.length > 120 ? `${text.slice(0, 120)}…` : text;
    },
  }),
  columnHelper.accessor('score', { header: 'Score' }),
];

function QuestionsList() {
  const { lesson } = Route.useSearch();

  // `lesson_questions` now carries two student predicates — enrolment and
  // all-topics-complete (content/service.py `require_course_access` /
  // `require_lesson_quiz_unlocked`). Both are implications over
  // `isinstance(user, Student)`, so an admin — who holds no course grant and no
  // topic completions — passes. A 403 here means one of them was rewritten as a
  // conjunction, not that this screen is misconfigured.
  const { data, isLoading, error } = api.lessonQuestions.useQuery({
    path: { lesson_id: lesson },
  });

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Message>Loading questions...</Message>;
  if (error) return <Message $error>Error loading questions</Message>;

  return (
    <div>
      <BackLink>
        <Link to="/lessons/$lessonId" params={{ lessonId: String(lesson) }}>
          &larr; Back to lesson
        </Link>
      </BackLink>
      <PageTitle>Questions</PageTitle>

      <Table
        table={table}
        to="/questions/$questionId"
        getParams={(row) => ({ questionId: String(row.id) })}
      />
    </div>
  );
}

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
